import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Input, Label } from 'reactstrap';

import apiOffers from 'api/offers';
import IOfferProductsGet from 'api/interfaces/offers/IOfferProductsGet';
import PrimaryButton from 'components/layout/buttons/PrimaryButton';
import { moveItemWithinSubset } from 'services/arrays/moveItem';
import translations from 'services/translations/translations';
import { addMessage } from 'store/messages/actions';
import { fetchInstallationCategories } from 'store/products/actions';
import { WRITE_OFFERS } from 'services/enums/users/permissions';
import useHasPermission from 'services/users/useHasPermission';

import OfferProductsTable from './OfferProductsTable';
import SavePriceListTemplate from './SavePriceListTemplate';

import 'style/offers/OfferPriceList.css';

const ADDITIONAL_OFFER = 'additional-offer';

/**
 * The additional price list of the offer: rows calculated like positions (materials, work,
 * freight), printed on a page of their own after the offer by their name and unit price.
 * A row can have a price text instead (e.g. "25 %"); a row with neither is a note.
 */
const OfferPriceList = ({
  locked,
  offerId,
  products,
  setProducts,
  setShowQuantities,
  showQuantities,
  type,
}: {
  locked: boolean;
  offerId: number;
  // All rows of the offer (positions and price list rows), null until loaded
  products: null | IOfferProductsGet[];
  setProducts: (products: IOfferProductsGet[]) => void;
  // Kept by the offer page, as this section is unmounted while closed
  setShowQuantities: (showQuantities: boolean) => void;
  // The quantities are printed in the price list of the PDF
  showQuantities: boolean;
  type: string;
}): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation([translations.offers, translations.common]);

  const [reordering, setReordering] = useState(false);
  const [savingSettings, setSavingSettings] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [registryOpen, setRegistryOpen] = useState(false);
  const canWriteOffers = useHasPermission(WRITE_OFFERS);

  // The rows are shared with the positions section: a failed load leaves them unloaded
  // (not empty), so the positions section loads them itself
  const loadProducts = async () => {
    try {
      setProducts(
        type === ADDITIONAL_OFFER
          ? await apiOffers.products.getForAdditionalOffer(offerId)
          : await apiOffers.products.getForOffer(offerId)
      );
      setLoadFailed(false);
    } catch (error) {
      setLoadFailed(true);
    }
  };

  // Needed to count only installations (not other expenses) in the hours
  useEffect(() => {
    dispatch(fetchInstallationCategories());
  }, [dispatch]);

  useEffect(() => {
    if (products === null) {
      loadProducts();
    }
  }, []);

  const rows =
    products === null
      ? []
      : products.filter((product) => product.isPriceListItem === true);

  const canReorder = !locked && rows.length > 1;

  // Moves a row within its work type and saves the order at once (as in the PDF)
  const moveRow = async (group: string, fromIndex: number, toIndex: number) => {
    if (products === null) {
      return;
    }

    const reordered = moveItemWithinSubset(
      products,
      (product) => product.group === group && product.isPriceListItem === true,
      fromIndex,
      toIndex
    );
    setProducts(reordered);

    try {
      const ids = reordered
        .filter((product) => product.isPriceListItem === true)
        .map((product) => product.id);

      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.positionOrder.put(offerId, ids);
      } else {
        await apiOffers.positionOrder.put(offerId, ids);
      }
    } catch (error) {
      dispatch(
        addMessage(t(`${translations.offers}:Saving position order failed`))
      );
      loadProducts();
    }
  };

  const changeShowQuantities = async (value: boolean) => {
    setSavingSettings(true);
    setShowQuantities(value);

    try {
      if (type === ADDITIONAL_OFFER) {
        await apiOffers.additionalOffers.patchPriceListSettings(offerId, value);
      } else {
        await apiOffers.patchPriceListSettings(offerId, value);
      }
    } catch (error) {
      setShowQuantities(!value);
      dispatch(addMessage(t(`${translations.offers}:priceList.Save failed`)));
    } finally {
      setSavingSettings(false);
    }
  };

  return (
    <div className="offer-price-list">
      <p className="offer-price-list-info">
        {t(`${translations.offers}:priceList.Info`)}
      </p>
      <div className="offer-price-list-toolbar">
        <Label check className="offer-price-list-checkbox">
          <Input
            type="checkbox"
            checked={showQuantities}
            disabled={locked || savingSettings}
            onChange={(event) => changeShowQuantities(event.target.checked)}
          />
          {t(`${translations.offers}:priceList.Show quantities`)}
        </Label>
        {/* The rows to the registry, from which a department's new offers can get them */}
        {rows.length > 0 && canWriteOffers ? (
          <PrimaryButton onClick={() => setRegistryOpen(true)} size="sm">
            {t(`${translations.offers}:priceListRegistry.Button`)}
          </PrimaryButton>
        ) : null}
        {canReorder ? (
          <PrimaryButton
            className={reordering ? 'position-order-active' : undefined}
            onClick={() => setReordering(!reordering)}
            size="sm"
          >
            {reordering
              ? t(`${translations.offers}:Position order done`)
              : t(`${translations.offers}:priceList.Change order`)}
          </PrimaryButton>
        ) : null}
      </div>
      {loadFailed ? (
        <p className="offer-price-list-empty">
          {t(`${translations.offers}:priceList.Load failed`)}
        </p>
      ) : null}
      {products === null ? null : (
        <>
          {rows.length === 0 ? (
            <p className="offer-price-list-empty">
              {t(`${translations.offers}:priceList.No rows`)}
            </p>
          ) : (
            <OfferProductsTable
              move={moveRow}
              priceList
              products={rows}
              reordering={reordering && canReorder}
              type={type}
            />
          )}
        </>
      )}
      {locked ? null : (
        <div className="offer-price-list-actions">
          <PrimaryButton
            className="button-one-line"
            onClick={() =>
              history.push(
                `/offers/${offerId}/${type}/products/create?priceList=true`
              )
            }
          >
            {t(`${translations.offers}:priceList.Add row`).toLocaleUpperCase()}
          </PrimaryButton>
        </div>
      )}
      <SavePriceListTemplate
        additionalOffer={type === ADDITIONAL_OFFER}
        isOpen={registryOpen}
        offerId={offerId}
        toggle={() => setRegistryOpen(!registryOpen)}
      />
    </div>
  );
};

export default OfferPriceList;
